import { FC, MouseEventHandler, useMemo, useState } from "react";
import moment from "moment";
import { useDidMount, useWillUnmount } from "rooks";
import { Skeleton, Stack, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { BillingCost, NullableDate } from "@/services/cloudchipr.api";
import { formatDate } from "@/utils/helpers/date-time/datetime-format";
import { billingCostHighchartsDataGenerator } from "@/components/pages/common/resources-ag-grid/utils/billing-cost/billingCostHighchartsDataGenerator";
import { moneyFormatter } from "@/utils/numeral/moneyFormatter";
import { BillingCostChart } from "@/components/pages/common/resources-ag-grid/components/column-defs/column-def-cell-renderers/billing-cost/BillingCostChart";
import { BillingCostNoDataChip } from "@/components/pages/common/resources-ag-grid/components/column-defs/column-def-cell-renderers/billing-cost/BillingCostNoDataChip";

interface BillingCostCellProps {
  state?: string;
  createdAt?: NullableDate;
  billingCost?: BillingCost;
  onClick?: MouseEventHandler;
}

export const BillingCostCell: FC<BillingCostCellProps> = ({
  state,
  onClick,
  createdAt,
  billingCost,
}) => {
  const [renderChart, setRenderChart] = useState(false);

  const { startDate, endDate } = useMemo(() => {
    const startPoint = billingCost?.start_point;
    const endPoint = moment(billingCost?.start_point ?? "").add(30, "day");

    return {
      startDate: formatDate(startPoint ?? "", { type: "date" }),
      endDate: formatDate(endPoint, { type: "date" }),
    };
  }, [billingCost?.start_point]);

  const chartData = useMemo(() => {
    if (!billingCost) {
      return;
    }

    const allIsNull =
      (billingCost.daily_costs.split(",")?.filter((item) => item !== "null")
        ?.length ?? 0) === 0;

    if (allIsNull) {
      return;
    }

    return billingCostHighchartsDataGenerator(
      billingCost.start_point,
      billingCost.daily_costs,
    );
  }, [billingCost]);

  useDidMount(() => {
    setTimeout(() => setRenderChart(true), 500);
  });

  useWillUnmount(() => {
    setRenderChart(false);
  });

  if (!renderChart) {
    return (
      <Stack alignItems="end" width="100%">
        <Skeleton height={50} width="100%" />
      </Stack>
    );
  }

  if (!chartData?.series?.length || !billingCost?.daily_costs) {
    let title = "N/A";
    let tooltipTitle = "Billing data is not available for this resource.";

    if (state === "stopped" || state === "stopping") {
      title = "Resource is stopped";
      tooltipTitle =
        "Billing data is not available. This resource is currently stopped.";
    }

    if (createdAt && moment(createdAt).isSame(moment(), "day")) {
      title = "New Resource";
      tooltipTitle =
        "Billing data is not available. This resource was recently created or launched.";
    }

    return <BillingCostNoDataChip title={title} tooltipTitle={tooltipTitle} />;
  }

  return (
    <Stack
      width="100%"
      height="100%"
      role="button"
      tabIndex={0}
      direction="column"
      justifyContent="center"
      onClick={onClick}
      sx={{ cursor: "pointer" }}
    >
      <Stack direction="row" pt={1} alignItems="center" spacing={0.5}>
        <Typography variant="caption" lineHeight={1}>
          Past 30d Cost:{" "}
          <Typography variant="inherit" component="span" fontWeight="bold">
            {moneyFormatter(billingCost.total)}
          </Typography>
        </Typography>
        <Tooltip
          arrow
          placement="top"
          title={`Billing Cost for past 30 days ${startDate} to ${endDate}`}
        >
          <InfoOutlinedIcon color="action" sx={{ fontSize: 14 }} />
        </Tooltip>
      </Stack>

      <BillingCostChart chartData={chartData} />
    </Stack>
  );
};
